"use client";

import { useState, useEffect, useCallback, useMemo } from "react";
import { useWallet as useSolanaWallet } from "@solana/wallet-adapter-react";
import { useWallet, useAccount } from "@getpara/react-sdk";
import { Connection, PublicKey, Transaction } from "@solana/web3.js";
import { toast } from "sonner";

import {
  executeBurn,
  fetchTokenAccounts,
  fetchEmptyTokenAccounts,
  fetchNFTs,
  closeEmptyAccountsBatch,
  buildCloseAccountsTransaction,
  fetchWithheldFeeInfo,
  checkSignatureStatus,
  waitForConfirmation,
  burnNFTs,
  getSolBalance,
  formatTokenAmount,
  formatSol,
  parseTokenAmount,
  addBurn,
  createBurnRecord,
  TYPICAL_RENT_PER_ACCOUNT,
  FORGEPAD_FEE_PERCENT,
  type TokenAccountInfo,
  type EmptyTokenAccount,
  type NFTInfo,
  type BurnStatus,
  type BuildCloseAccountsTxResult,
  type CloseAccountInput,
} from "@/lib/burn";

import {
  BurnTokenList,
  BurnAmountInput,
  BurnConfirmModal,
  BurnStatusOverlay,
  RecentBurns,
  EmptyAccountsSection,
  NFTsSection,
} from "@/components/burn";

import { useParaTransactions } from "@/hooks/use-para-transactions";
import { Loader2, Flame, Trash2, Image as ImageIcon } from "lucide-react";
import Image from "next/image";

type TabId = "burn-tokens" | "close-accounts" | "nfts";

// Disabled until forgeswap.fun is Blowfish-allowlisted — the multi-transaction
// signAll prompt triggers Phantom's malicious-dApp warning on our domain.
// When true: one wallet prompt signs all batches, then submits sequentially.
// When false: each batch gets its own signAndSendTransaction prompt, auto-continues.
const USE_SIGN_ALL_SIGNING = false;

export default function BurnPage() {
  // Wallet hooks - use Para SDK as primary, wallet-adapter as fallback
  const { publicKey: solanaAdapterPublicKey } = useSolanaWallet();
  const { data: paraWallet } = useWallet();
  const { isConnected: paraIsConnected } = useAccount();
  const { onSendTransaction } = useParaTransactions();

  // Derive Solana address using same pattern as use-token-balances.tsx
  // Para wallet takes precedence, then fall back to wallet adapter
  const solanaWalletAddress = useMemo(() => {
    // If Para has a Solana wallet connected, use it
    if (paraWallet?.address && paraWallet?.type === "SOLANA") {
      return paraWallet.address;
    }
    // Fall back to wallet adapter (external wallets like Phantom)
    return solanaAdapterPublicKey?.toBase58() || null;
  }, [paraWallet?.address, paraWallet?.type, solanaAdapterPublicKey]);

  // Connected if Para is connected OR if we have a wallet adapter public key
  const isConnected = paraIsConnected || !!solanaAdapterPublicKey;

  // Convert address string to PublicKey for use in burn functions
  const publicKey = useMemo(() => {
    if (!solanaWalletAddress) return null;
    try {
      return new PublicKey(solanaWalletAddress);
    } catch {
      return null;
    }
  }, [solanaWalletAddress]);

  // Create connection using our RPC proxy
  const connection = useMemo(
    () =>
      new Connection(
        typeof window !== "undefined"
          ? `${window.location.origin}/api/rpc?chain=solana`
          : "https://api.mainnet-beta.solana.com",
        { commitment: "confirmed" }
      ),
    []
  );

  // Tab state
  const [activeTab, setActiveTab] = useState<TabId>("burn-tokens");

  // State
  const [tokens, setTokens] = useState<TokenAccountInfo[]>([]);
  const [selectedToken, setSelectedToken] = useState<TokenAccountInfo | null>(null);
  const [burnAmount, setBurnAmount] = useState("");
  const [solBalance, setSolBalance] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [burnStatus, setBurnStatus] = useState<BurnStatus | null>(null);
  const [burnHistoryRefresh, setBurnHistoryRefresh] = useState(0);

  // Empty accounts state
  const [emptyAccounts, setEmptyAccounts] = useState<EmptyTokenAccount[]>([]);
  const [isClosingAccounts, setIsClosingAccounts] = useState(false);

  // NFT state
  const [nfts, setNfts] = useState<NFTInfo[]>([]);
  const [isBurningNFTs, setIsBurningNFTs] = useState(false);

  // Fetch tokens, SOL balance, empty accounts, and NFTs
  // skipNFTCache: bypass server cache for NFTs (use after burns to get fresh data)
  const fetchData = useCallback(async (skipNFTCache: boolean = false) => {
    if (!publicKey || !isConnected) {
      setTokens([]);
      setSolBalance(0);
      setEmptyAccounts([]);
      setNfts([]);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [tokenList, sol, emptyList, nftList] = await Promise.all([
        fetchTokenAccounts(connection, publicKey),
        getSolBalance(connection, publicKey),
        fetchEmptyTokenAccounts(connection, publicKey),
        fetchNFTs(connection, publicKey, skipNFTCache),
      ]);
      setTokens(tokenList);
      setSolBalance(sol);
      setEmptyAccounts(emptyList);
      setNfts(nftList);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Failed to load tokens. Please try again.");
      toast.error("Failed to load tokens");
    } finally {
      setLoading(false);
    }
  }, [publicKey, isConnected, connection]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // DIAGNOSTIC: Watch for wallet disconnect
  useEffect(() => {
    console.log("[disconnect-debug] WALLET STATE CHANGED", {
      solanaAdapterPublicKey: solanaAdapterPublicKey?.toBase58() || null,
      paraWallet_address: paraWallet?.address || null,
      paraIsConnected,
      isConnected,
      timestamp: new Date().toISOString(),
    });
  }, [solanaAdapterPublicKey, paraWallet?.address, paraIsConnected, isConnected]);

  // Select token
  const handleSelectToken = (token: TokenAccountInfo) => {
    setSelectedToken(token);
    setBurnAmount("");
  };

  // Validate burn amount
  const isValidAmount = useCallback(() => {
    if (!selectedToken || !burnAmount) return false;
    const amount = parseFloat(burnAmount);
    if (isNaN(amount) || amount <= 0) return false;
    const maxAmount = parseFloat(formatTokenAmount(selectedToken.amount, selectedToken.decimals));
    return amount <= maxAmount;
  }, [selectedToken, burnAmount]);

  // Open confirmation modal
  const handleBurnClick = () => {
    if (!isValidAmount()) return;
    setShowConfirmModal(true);
  };

  // Fix 2: "Burn all & close" — skip amount input, go straight to confirm
  const handleBurnAllAndClose = () => {
    if (!selectedToken) return;
    const maxAmount = formatTokenAmount(selectedToken.amount, selectedToken.decimals);
    setBurnAmount(maxAmount);
    setShowConfirmModal(true);
  };

  // Adapter for sendTransaction - wraps onSendTransaction to match executeBurn's expected signature
  const sendTransactionAdapter = useCallback(
    async (transaction: Transaction): Promise<string> => {
      const signature = await onSendTransaction(transaction);
      if (!signature) {
        throw new Error("Transaction signature was not returned");
      }
      return signature;
    },
    [onSendTransaction]
  );

  // Execute burn
  const handleConfirmBurn = async () => {
    if (!publicKey || !selectedToken || !burnAmount) return;

    // Fix 3: SOL balance pre-check — need enough for tx fees + priority fee buffer
    const MIN_SOL_FOR_TX = 1_000_000; // 0.001 SOL in lamports
    if (solBalance < MIN_SOL_FOR_TX) {
      toast.error("You need at least 0.001 SOL to cover network fees. Please add SOL to your wallet and try again.");
      return;
    }

    setShowConfirmModal(false);

    // Check if burning 100% of balance — if so, close the account to reclaim rent
    const maxAmount = formatTokenAmount(selectedToken.amount, selectedToken.decimals);
    const isFullBurn = burnAmount === maxAmount;

    const result = await executeBurn(
      connection,
      selectedToken.pubkey,
      selectedToken.mint,
      publicKey,
      burnAmount,
      selectedToken.decimals,
      selectedToken.programId,
      sendTransactionAdapter,
      (status) => setBurnStatus(status),
      isFullBurn // closeAccount flag
    );

    if (result.success && result.signature) {
      // Save burn to history
      const burnRecord = createBurnRecord(
        result.signature,
        selectedToken.mint.toBase58(),
        burnAmount,
        selectedToken.decimals,
        selectedToken.name,
        selectedToken.symbol,
        selectedToken.logoURI
      );
      addBurn(burnRecord);
      setBurnHistoryRefresh((prev) => prev + 1);

      // Log burn to admin tracking (fire-and-forget, USD calculated server-side)
      // Fix 1: Use BigInt for precision — for full burn use raw amount directly,
      // for partial burn use parseTokenAmount which handles BigInt correctly
      const rawAmount = isFullBurn
        ? selectedToken.amount.toString()
        : parseTokenAmount(burnAmount, selectedToken.decimals).toString();
      fetch("/api/burns/log", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          walletAddress: publicKey?.toBase58(),
          tokenAddress: selectedToken.mint.toBase58(),
          tokenSymbol: selectedToken.symbol || null,
          tokenName: selectedToken.name || null,
          amount: rawAmount,
          decimals: selectedToken.decimals,
          txHash: result.signature,
          burnType: "user_burn",
        }),
      }).catch((err) => console.error("[Burn Log] Failed:", err));

      // Success toast — include rent reclaim note if account was closed
      if (result.accountClosed) {
        const netReclaim = TYPICAL_RENT_PER_ACCOUNT * (1 - FORGEPAD_FEE_PERCENT / 100);
        toast.success(`Burned ${burnAmount} ${selectedToken.symbol || "tokens"}, reclaimed ${netReclaim.toFixed(4)} SOL`);
      } else {
        toast.success(`Burned ${burnAmount} ${selectedToken.symbol || "tokens"}!`);
      }

      setBurnAmount("");
      setSelectedToken(null);
      await fetchData();
    } else {
      toast.error(result.error || "Burn failed");
    }

    setBurnStatus(null);
  };

  // Close empty accounts - processes batches of ~10 accounts each
  // Per-batch signing: each batch prompts signAndSendTransaction, auto-continues to next
  // SignAll path (disabled): one prompt signs all batches, then submits sequentially
  const handleCloseEmptyAccounts = async (accounts: EmptyTokenAccount[]) => {
    if (!publicKey || accounts.length === 0) return;

    setIsClosingAccounts(true);

    const BATCH_SIZE = 10; // Must match MAX_ACCOUNTS_PER_TX in burn.ts
    const totalAccounts = accounts.length;
    const totalBatches = Math.ceil(totalAccounts / BATCH_SIZE);

    // Check if wallet supports signAllTransactions
    const solanaWallet = typeof window !== "undefined" ? (window as any).solana : null;
    const solflareWallet = typeof window !== "undefined" ? (window as any).solflare : null;
    const connectedPubkey = solanaWalletAddress;

    const phantomPubkey = solanaWallet?.publicKey?.toString();
    const solflarePubkey = solflareWallet?.publicKey?.toString();

    const isPhantomConnected = !!solanaWallet?.isPhantom && phantomPubkey === connectedPubkey;
    const isSolflareConnected = !!solflareWallet && solflarePubkey === connectedPubkey;

    const walletWithSignAll = isPhantomConnected ? solanaWallet : (isSolflareConnected ? solflareWallet : null);
    const hasSignAllTransactions = typeof walletWithSignAll?.signAllTransactions === "function";

    console.log("[Close Accounts] Wallet detection:", {
      isPhantomConnected,
      isSolflareConnected,
      hasSignAllTransactions,
      totalBatches,
      useSignAll: USE_SIGN_ALL_SIGNING,
    });

    // Use per-batch signing unless USE_SIGN_ALL_SIGNING is enabled AND wallet supports it
    if (!USE_SIGN_ALL_SIGNING || !hasSignAllTransactions) {
      console.log("[Close Accounts] Using per-batch signing", { useSignAll: USE_SIGN_ALL_SIGNING, hasSignAllTransactions });
      await handleCloseEmptyAccountsPerBatch(accounts);
      return;
    }

    // === BATCH SIGNING PATH (currently disabled via USE_SIGN_ALL_SIGNING) ===
    // Build all transactions, sign once, submit sequentially

    let totalClosed = 0;
    let totalRentReclaimed = 0;
    let totalFeePaid = 0;
    let stoppedEarly = false;
    let stopReason: string | undefined;

    try {
      // Phase 1: Build ALL batch transactions (FAST — no RPC calls, synchronous)
      const buildStart = performance.now();
      setBurnStatus({ stage: "preparing", message: `Building ${totalBatches} transactions...` });

      const builtTxs: Array<{
        transaction: Transaction;
        batchIndex: number;
        accountCount: number;
        rentLamports: number;
        feeLamports: number;
        accounts: Array<{ pubkey: PublicKey; programId: PublicKey }>;
        batchAccounts: EmptyTokenAccount[];
      }> = [];

      // Fetch withheld fee info ONCE for all Token-2022 accounts (chunked at 100)
      const allAccountInputs: CloseAccountInput[] = accounts.map(a => ({
        pubkey: a.pubkey,
        programId: a.programId,
        mint: a.mint,
      }));
      const withheldFeeInfo = await fetchWithheldFeeInfo(connection, allAccountInputs);

      // Build all transactions synchronously with placeholder blockhash
      // Real blockhash applied below, right before signing (freshest possible)
      const placeholderBlockhash = "11111111111111111111111111111111";

      for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
        const batchStart = batchIndex * BATCH_SIZE;
        const batchEnd = Math.min(batchStart + BATCH_SIZE, totalAccounts);
        const batchAccounts = accounts.slice(batchStart, batchEnd);

        // Synchronous build — uses pre-fetched withheld fee data
        const buildResult = buildCloseAccountsTransaction(
          batchAccounts.map((a): CloseAccountInput => ({ pubkey: a.pubkey, programId: a.programId, mint: a.mint })),
          publicKey,
          placeholderBlockhash,
          withheldFeeInfo
        );

        if (!buildResult.success || !buildResult.transaction) {
          console.warn(`[Close Accounts] Failed to build batch ${batchIndex + 1}:`, buildResult.error);
          continue;
        }

        builtTxs.push({
          transaction: buildResult.transaction,
          batchIndex,
          accountCount: buildResult.accountCount,
          rentLamports: buildResult.rentLamports,
          feeLamports: buildResult.feeLamports,
          accounts: buildResult.accounts,
          batchAccounts,
        });
      }

      if (builtTxs.length === 0) {
        throw new Error("No valid transactions could be built");
      }

      // Fetch ONE blockhash for ALL transactions, right before signing (freshest)
      const { blockhash } = await connection.getLatestBlockhash("confirmed");
      for (const tx of builtTxs) {
        tx.transaction.recentBlockhash = blockhash;
      }

      const buildTime = performance.now() - buildStart;
      console.log(`[Close Accounts] Built ${builtTxs.length} transactions in ${buildTime.toFixed(0)}ms`);

      // Phase 2: Sign ALL transactions with ONE wallet prompt
      setBurnStatus({
        stage: "signing",
        message: `Approve ${builtTxs.length} transaction${builtTxs.length > 1 ? "s" : ""} in your wallet...`
      });

      const transactions = builtTxs.map(t => t.transaction);
      let signedTransactions: Transaction[];

      try {
        signedTransactions = await walletWithSignAll.signAllTransactions(transactions);
        console.log(`[Close Accounts] Signed ${signedTransactions.length} transactions`);
      } catch (signError: any) {
        const errorMsg = signError?.message || String(signError);
        if (
          errorMsg.includes("User rejected") ||
          errorMsg.includes("rejected") ||
          errorMsg.includes("cancelled") ||
          errorMsg.includes("canceled")
        ) {
          console.log("[Close Accounts] User rejected batch signing");
          setBurnStatus(null);
          setIsClosingAccounts(false);
          return; // Clean cancel - user declined
        }
        throw signError;
      }

      // Phase 3: Submit signed transactions SEQUENTIALLY with confirmation
      for (let i = 0; i < signedTransactions.length; i++) {
        const signedTx = signedTransactions[i];
        const txInfo = builtTxs[i];
        const batchNum = i + 1;

        setBurnStatus({
          stage: "confirming",
          message: `Submitting transaction ${batchNum} of ${signedTransactions.length}... (${totalClosed} of ${totalAccounts} closed)`,
        });

        try {
          // Send the pre-signed transaction
          // NOTE: Using sendRawTransaction (not signAndSendTransaction) is intentional here.
          // signAllTransactions returns pre-signed txs that must be submitted via sendRawTransaction.
          // This pattern may trigger Blowfish warnings, but is required for one-prompt batch signing.
          const signature = await connection.sendRawTransaction(signedTx.serialize(), {
            skipPreflight: false,
            preflightCommitment: "confirmed",
          });

          console.log(`[Close Accounts] Batch ${batchNum} sent:`, signature);

          // Wait for confirmation
          setBurnStatus({
            stage: "confirming",
            message: `Confirming transaction ${batchNum} of ${signedTransactions.length}...`,
            signature,
          });

          const confirmResult = await waitForConfirmation(connection, signature, 60000);

          if (confirmResult.success) {
            totalClosed += txInfo.accountCount;
            totalRentReclaimed += txInfo.rentLamports - txInfo.feeLamports;
            totalFeePaid += txInfo.feeLamports;

            // Log each account close to admin tracking
            const solPerAccount = TYPICAL_RENT_PER_ACCOUNT;
            const feePerAccount = solPerAccount * (FORGEPAD_FEE_PERCENT / 100);
            const batchStart = txInfo.batchIndex * BATCH_SIZE;
            txInfo.batchAccounts.slice(0, txInfo.accountCount).forEach((account, j) => {
              fetch("/api/burns/log", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  walletAddress: publicKey?.toBase58(),
                  tokenAddress: account.mint.toBase58(),
                  amount: "0",
                  decimals: 0,
                  solReclaimed: solPerAccount,
                  forgeFeeSol: feePerAccount,
                  feePercent: FORGEPAD_FEE_PERCENT / 100,
                  txHash: `${signature}-${batchStart + j}`,
                  burnType: "account_close",
                }),
              }).catch((err) => console.error("[Burn Log] Failed:", err));
            });

            console.log(`[Close Accounts] Batch ${batchNum} confirmed: ${txInfo.accountCount} accounts closed`);
          } else {
            // On-chain failure - stop processing
            stoppedEarly = true;
            stopReason = confirmResult.status || "Transaction failed on-chain";
            console.error(`[Close Accounts] Batch ${batchNum} failed:`, confirmResult.status);
            break;
          }
        } catch (submitError: any) {
          const errorMsg = submitError?.message || String(submitError);

          // Check for blockhash expiry
          if (
            errorMsg.includes("blockhash not found") ||
            errorMsg.includes("BlockhashNotFound") ||
            errorMsg.includes("expired")
          ) {
            // Blockhash expired - tell user to re-approve remaining transactions
            const remaining = signedTransactions.length - i;
            stoppedEarly = true;
            stopReason = `Transactions expired. ${remaining} transaction${remaining > 1 ? "s" : ""} need re-approval.`;
            console.warn(`[Close Accounts] Blockhash expired at batch ${batchNum}`);
            toast.error(`Transactions expired — please try again for the remaining ${totalAccounts - totalClosed} accounts`);
            break;
          }

          stoppedEarly = true;
          stopReason = errorMsg;
          console.error(`[Close Accounts] Batch ${batchNum} submit error:`, submitError);
          break;
        }
      }

      // Show final result
      if (totalClosed > 0) {
        const netSol = (totalRentReclaimed / 1e9).toFixed(4);
        if (stoppedEarly) {
          toast.success(`Closed ${totalClosed} of ${totalAccounts} accounts, reclaimed ${netSol} SOL`);
          if (stopReason && !stopReason.includes("expired")) {
            toast.error(`Stopped: ${stopReason}`);
          }
        } else {
          toast.success(`Closed ${totalClosed} accounts, reclaimed ${netSol} SOL`);
        }
        await fetchData();
      } else if (stoppedEarly && stopReason) {
        toast.error(stopReason);
      } else {
        toast.error("Failed to close accounts");
      }
    } catch (error: any) {
      console.error("[Close Accounts] Batch signing error:", error);
      toast.error(error?.message || "Failed to close accounts");
    }

    setBurnStatus(null);
    setIsClosingAccounts(false);
  };

  // Per-batch signing: each batch gets its own wallet prompt, auto-continues to next batch
  const handleCloseEmptyAccountsPerBatch = async (accounts: EmptyTokenAccount[]) => {
    const BATCH_SIZE = 10;
    const totalAccounts = accounts.length;
    const totalBatches = Math.ceil(totalAccounts / BATCH_SIZE);

    let totalClosed = 0;
    let totalRentReclaimed = 0;
    let totalFeePaid = 0;
    let stoppedEarly = false;
    let stopReason: string | undefined;

    for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
      const batchStart = batchIndex * BATCH_SIZE;
      const batchEnd = Math.min(batchStart + BATCH_SIZE, totalAccounts);
      const batchAccounts = accounts.slice(batchStart, batchEnd);
      const batchNum = batchIndex + 1;

      setBurnStatus({
        stage: "preparing",
        message: totalBatches > 1
          ? `Closing accounts... ${totalClosed} of ${totalAccounts} (transaction ${batchNum} of ${totalBatches})`
          : `Closing ${batchAccounts.length} accounts...`,
      });

      try {
        const result = await closeEmptyAccountsBatch(
          connection,
          batchAccounts.map((a): CloseAccountInput => ({ pubkey: a.pubkey, programId: a.programId, mint: a.mint })),
          publicKey!,
          sendTransactionAdapter,
          (status) => {
            if (totalBatches > 1 && status.stage !== "error") {
              setBurnStatus({
                ...status,
                message: `${status.message} (batch ${batchNum}/${totalBatches})`,
              });
            } else {
              setBurnStatus(status);
            }
          }
        );

        if (result.success && result.accountsClosed > 0) {
          totalClosed += result.accountsClosed;
          totalRentReclaimed += result.rentReclaimed;
          totalFeePaid += result.feePaid;

          const solPerAccount = TYPICAL_RENT_PER_ACCOUNT;
          const feePerAccount = solPerAccount * (FORGEPAD_FEE_PERCENT / 100);
          batchAccounts.slice(0, result.accountsClosed).forEach((account, i) => {
            fetch("/api/burns/log", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                walletAddress: publicKey?.toBase58(),
                tokenAddress: account.mint.toBase58(),
                amount: "0",
                decimals: 0,
                solReclaimed: solPerAccount,
                forgeFeeSol: feePerAccount,
                feePercent: FORGEPAD_FEE_PERCENT / 100,
                txHash: `${result.signature}-${batchStart + i}`,
                burnType: "account_close",
              }),
            }).catch((err) => console.error("[Burn Log] Failed:", err));
          });
        } else if (!result.success) {
          stoppedEarly = true;
          stopReason = result.error || "Transaction failed";
          break;
        }
      } catch (error: any) {
        const errorMsg = error?.message || String(error);
        if (
          errorMsg.includes("User rejected") ||
          errorMsg.includes("rejected") ||
          errorMsg.includes("cancelled") ||
          errorMsg.includes("canceled")
        ) {
          stoppedEarly = true;
          stopReason = "Transaction cancelled by user";
        } else {
          stoppedEarly = true;
          stopReason = errorMsg;
        }
        break;
      }
    }

    if (totalClosed > 0) {
      const netSol = (totalRentReclaimed / 1e9).toFixed(4);
      if (stoppedEarly) {
        toast.success(`Closed ${totalClosed} of ${totalAccounts} accounts, reclaimed ${netSol} SOL`);
        if (stopReason && !stopReason.includes("cancelled") && !stopReason.includes("rejected")) {
          toast.error(`Stopped: ${stopReason}`);
        }
      } else {
        toast.success(`Closed ${totalClosed} accounts, reclaimed ${netSol} SOL`);
      }
      await fetchData();
    } else if (stoppedEarly && stopReason) {
      if (!stopReason.includes("cancelled") && !stopReason.includes("rejected")) {
        toast.error(stopReason);
      }
    } else {
      toast.error("Failed to close accounts");
    }

    setBurnStatus(null);
    setIsClosingAccounts(false);
  };

  // Burn NFTs batch
  const handleBurnNFTs = async (nftsToburn: NFTInfo[]) => {
    if (!publicKey || nftsToburn.length === 0) return;

    setIsBurningNFTs(true);

    const result = await burnNFTs(
      connection,
      nftsToburn,
      publicKey,
      sendTransactionAdapter,
      (status) => setBurnStatus(status)
    );

    // DIAGNOSTIC: Check wallet state after tx confirms
    console.log("[disconnect-debug] tx returned from burnNFTs", {
      success: result.success,
      solanaAdapterPublicKey: solanaAdapterPublicKey?.toBase58(),
      paraWallet: paraWallet?.address,
      paraIsConnected,
    });

    if (result.success || result.nftsBurned > 0) {
      console.log("[disconnect-debug] starting burn history writes");
      // Record each burned NFT in burn history
      if (result.signature) {
        // Create a burn record for each NFT that was burned
        // Use the same signature for all (they're in the same batch/transaction)
        for (let i = 0; i < result.nftsBurned && i < nftsToburn.length; i++) {
          const nft = nftsToburn[i];
          const burnRecord = createBurnRecord(
            result.signature,
            nft.mint.toBase58(),
            "1", // NFTs always have amount 1
            0,   // NFTs have 0 decimals
            nft.name,
            "NFT",
            nft.image
          );
          addBurn(burnRecord);
        }
        console.log("[disconnect-debug] burn history written, calling setBurnHistoryRefresh");
        setBurnHistoryRefresh((prev) => prev + 1);
      }

      console.log("[disconnect-debug] showing toast");
      const netSol = (result.rentReclaimed / 1e9).toFixed(4);
      toast.success(`Burned ${result.nftsBurned} NFT${result.nftsBurned > 1 ? "s" : ""}, reclaimed ${netSol} SOL`);

      // DIAGNOSTIC: Check wallet state before fetchData
      console.log("[disconnect-debug] BEFORE fetchData", {
        solanaAdapterPublicKey: solanaAdapterPublicKey?.toBase58(),
        paraWallet: paraWallet?.address,
        paraIsConnected,
      });

      // Skip cache to get fresh NFT list after burn
      await fetchData(true);

      // DIAGNOSTIC: Check wallet state after fetchData
      console.log("[disconnect-debug] AFTER fetchData", {
        solanaAdapterPublicKey: solanaAdapterPublicKey?.toBase58(),
        paraWallet: paraWallet?.address,
        paraIsConnected,
      });
    } else {
      toast.error(result.error || "Failed to burn NFTs");
    }

    console.log("[disconnect-debug] setting burnStatus null and isBurningNFTs false");
    setBurnStatus(null);
    setIsBurningNFTs(false);

    // DIAGNOSTIC: Final state check
    console.log("[disconnect-debug] all post-burn ops complete", {
      solanaAdapterPublicKey: solanaAdapterPublicKey?.toBase58(),
      paraWallet: paraWallet?.address,
      paraIsConnected,
    });
  };

  // Tab configuration with counts
  const tabs: { id: TabId; label: string; shortLabel: string; count: number; icon: React.ReactNode }[] = [
    {
      id: "burn-tokens",
      label: "Burn Tokens",
      shortLabel: "Burn",
      count: tokens.length,
      icon: <Flame className="w-4 h-4" />,
    },
    {
      id: "close-accounts",
      label: "Close Accounts",
      shortLabel: "Close",
      count: emptyAccounts.length,
      icon: <Trash2 className="w-4 h-4" />,
    },
    {
      id: "nfts",
      label: "NFTs",
      shortLabel: "NFTs",
      count: nfts.length,
      icon: <ImageIcon className="w-4 h-4" />,
    },
  ];

  return (
    <div className="w-full min-h-screen bg-gradient-to-b from-black via-[#050505] to-[#0a0805] text-white pt-16 pb-20">
      {/* Header */}
      <header className="flex flex-col p-6 border-b border-[#1a1a1a] max-w-[600px] mx-auto mb-6">
        {/* Balance pill - own row, right-aligned */}
        {isConnected && publicKey && (
          <div className="flex justify-end mb-4">
            <div className="flex items-center gap-1.5 text-sm bg-[#111] border border-[#222] rounded-full px-4 py-2">
              <span className="text-[#666]">Balance:</span>
              <span className="text-[#f9cd4f] font-semibold">{formatSol(solBalance)} SOL</span>
            </div>
          </div>
        )}
        {/* Logo + description - full width, centered */}
        <div className="flex flex-col items-center text-center gap-3">
          <Image
            src="/forge-burner-logo.png"
            alt="Forge Burner"
            width={200}
            height={80}
            className="rounded-xl h-[64px] sm:h-[80px] w-auto object-contain"
          />
          <p className="text-sm text-[#888] max-w-[500px]">
            Permanently burn unwanted SPL tokens from your wallet. Removes scam airdrops, dust, and clutter — and reclaims the rent (small SOL refund) when the token account closes.
          </p>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[600px] mx-auto px-4 sm:px-8 py-6 bg-[rgba(10,10,10,0.8)] rounded-2xl border border-[#1a1a1a] shadow-[0_4px_40px_rgba(0,0,0,0.5),0_0_80px_rgba(255,140,0,0.03)]">
        {/* Not connected state */}
        {!isConnected ? (
          <div className="text-center py-20">
            <div className="text-5xl mb-4">🔗</div>
            <h2 className="text-xl font-semibold mb-2">Connect Your Wallet</h2>
            <p className="text-[#888]">Connect your Solana wallet to view burnable tokens</p>
            <p className="text-xs text-[#666] mt-4">
              Use the wallet button in the header to connect
            </p>
          </div>
        ) : loading ? (
          /* Loading state */
          <div className="flex flex-col gap-3 py-8">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-[72px] bg-[#111] rounded-xl animate-pulse"
              />
            ))}
            <div className="flex items-center justify-center gap-2 text-[#888] mt-4">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading tokens...
            </div>
          </div>
        ) : error ? (
          /* Error state */
          <div className="text-center py-20">
            <div className="text-5xl mb-4">⚠️</div>
            <h2 className="text-xl font-semibold mb-2 text-[#FF6B35]">Error Loading Tokens</h2>
            <p className="text-[#888] mb-4">{error}</p>
            <button
              onClick={() => fetchData()}
              className="px-6 py-2 bg-[#f9cd4f] text-black font-semibold rounded-lg hover:opacity-90 transition-opacity"
            >
              Try Again
            </button>
          </div>
        ) : (
          /* Connected state with tabs */
          <>
            {/* Tab Navigation */}
            <div className="flex border-b border-[#222] mb-6">
              {tabs.map((tab) => {
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-3 px-2 text-sm font-medium transition-all relative ${
                      isActive
                        ? "text-[#f9cd4f]"
                        : "text-[#666] hover:text-[#888]"
                    }`}
                  >
                    {tab.icon}
                    {/* Full label on desktop, short on mobile */}
                    <span className="hidden sm:inline">
                      {tab.label}
                      {tab.count > 0 && (
                        <span className="ml-1 text-xs opacity-70">({tab.count})</span>
                      )}
                    </span>
                    <span className="sm:hidden">
                      {tab.shortLabel}
                      {tab.count > 0 && (
                        <span className="ml-1 text-xs opacity-70">({tab.count})</span>
                      )}
                    </span>
                    {/* Active indicator */}
                    {isActive && (
                      <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#f9cd4f]" />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Tab Content */}
            <div className="min-h-[300px]">
              {/* Burn Tokens Tab */}
              {activeTab === "burn-tokens" && (
                <>
                  {tokens.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="text-4xl mb-3">📭</div>
                      <h3 className="text-lg font-semibold mb-2">No Tokens Found</h3>
                      <p className="text-sm text-[#888]">This wallet has no SPL tokens with balance</p>
                      <p className="text-xs text-[#666] mt-3">
                        Native SOL cannot be burned and is not shown here
                      </p>
                    </div>
                  ) : (
                    <>
                      <BurnTokenList
                        tokens={tokens}
                        selectedToken={selectedToken}
                        onSelectToken={handleSelectToken}
                        onRefresh={() => fetchData()}
                        isRefreshing={loading}
                      />

                      {selectedToken && (
                        <BurnAmountInput
                          token={selectedToken}
                          amount={burnAmount}
                          onAmountChange={setBurnAmount}
                          onBurnClick={handleBurnClick}
                          onBurnAllClick={handleBurnAllAndClose}
                          isValid={isValidAmount()}
                        />
                      )}

                      {/* Recent Burns */}
                      <hr className="border-[#1a1a1a] my-6" />
                      <RecentBurns refreshTrigger={burnHistoryRefresh} maxDisplay={5} />
                    </>
                  )}
                </>
              )}

              {/* Close Accounts Tab */}
              {activeTab === "close-accounts" && (
                <>
                  {emptyAccounts.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="text-4xl mb-3">✨</div>
                      <h3 className="text-lg font-semibold mb-2">No Empty Accounts</h3>
                      <p className="text-sm text-[#888]">No empty token accounts to close</p>
                      <p className="text-xs text-[#666] mt-3">
                        Burning 100% of a token&apos;s balance will close its account automatically
                      </p>
                    </div>
                  ) : (
                    <EmptyAccountsSection
                      emptyAccounts={emptyAccounts}
                      onCloseAccounts={handleCloseEmptyAccounts}
                      onRefresh={() => fetchData()}
                      isRefreshing={loading}
                      isClosing={isClosingAccounts}
                    />
                  )}
                </>
              )}

              {/* NFTs Tab */}
              {activeTab === "nfts" && (
                <>
                  {nfts.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="text-4xl mb-3">🖼️</div>
                      <h3 className="text-lg font-semibold mb-2">No NFTs Found</h3>
                      <p className="text-sm text-[#888]">This wallet has no burnable NFTs</p>
                      <p className="text-xs text-[#666] mt-3">
                        Compressed NFTs (cNFTs) are not yet supported
                      </p>
                    </div>
                  ) : (
                    <NFTsSection
                      nfts={nfts}
                      onBurnNFTs={handleBurnNFTs}
                      onRefresh={() => fetchData()}
                      isRefreshing={loading}
                      isBurning={isBurningNFTs}
                    />
                  )}
                </>
              )}
            </div>
          </>
        )}
      </main>

      {/* Confirmation Modal */}
      {showConfirmModal && selectedToken && (
        <BurnConfirmModal
          token={selectedToken}
          amount={burnAmount}
          onConfirm={handleConfirmBurn}
          onCancel={() => setShowConfirmModal(false)}
        />
      )}

      {/* Status Overlay */}
      {burnStatus && <BurnStatusOverlay status={burnStatus} />}
    </div>
  );
}
